import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { useIsFocused } from 'record-it-components/hooks/useIsFocused';
import { SearchInput } from 'record-it-components/input/SearchInput/SearchInput';
import { CreateEntityButton } from 'record-it-components/misc/CreateEntityButton/CreateEntityButton';
import { PageContentContainer } from 'record-it-components/pageContentLayout/PageContentContainer/PageContentContainer';
import { PageContentHeader } from 'record-it-components/pageContentLayout/PageContentHeader/PageContentHeader';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { useRequiredWorkTimeEntryToCreate } from '../../../hooks/createWorkTimeEntry/useRequiredWorkTimeEntryToCreate';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import styles from './CreateWorkTimeEntrySelectConstructionSitePage.module.css';
import { CreateWorkTimeEntrySelectConstructionSitePageDefaultContent } from './CreateWorkTimeEntrySelectConstructionSitePageDefaultContent';
import { CreateWorkTimeEntrySelectConstructionSitePageSearchContent } from './CreateWorkTimeEntrySelectConstructionSitePageSearchContent';
export type CreateWorkTimeEntrySelectConstructionSitePageProps = PropsWithoutChildren<Record<never, never>>;
export const CreateWorkTimeEntrySelectConstructionSitePage: FunctionComponent<CreateWorkTimeEntrySelectConstructionSitePageProps> = React.memo(props => {
  const $ = _c(30);
  prepareProps({
    props,
    ignoreChildren: true
  });
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const workTimeEntryToCreate = useRequiredWorkTimeEntryToCreate();
  const {
    getRequiredCurrentUserPermissions
  } = useCurrentUser();
  useConfigureBreadcrumbsWithBuilder(_temp);
  const [searchText, setSearchText] = useState("");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    handleFocus: handleSearchInputFocus,
    handleBlur: handleSearchInputBlur,
    isFocused: searchInputFocused
  } = useIsFocused(t0);
  const t1 = useEntities(EntityName.ConstructionSite, "getByUserGroupId", workTimeEntryToCreate.userGroupId);
  let t2;
  if ($[1] !== t1) {
    t2 = t1.filter(_temp2);
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const constructionSites = t2;
  const showSearchContent = searchInputFocused || searchText;
  let t3;
  if ($[3] !== navigate) {
    t3 = () => {
      navigate(RoutePaths.createWorkTimeEntryCreateConstructionSite());
    };
    $[3] = navigate;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleCreateConstructionSiteClicked = useEventCallback(t3);
  let t4;
  if ($[5] !== getRequiredCurrentUserPermissions || $[6] !== handleCreateConstructionSiteClicked || $[7] !== t || $[8] !== workTimeEntryToCreate.userGroupId) {
    t4 = () => {
      const permissions = getRequiredCurrentUserPermissions();
      if (!permissions.canEditConstructionSitesOfUserGroup(workTimeEntryToCreate.userGroupId)) {
        return [];
      }
      return [<CreateEntityButton key="HomePage.CreateWorkTimeEntryButton" text={t("components.page.CreateWorkTimeEntrySelectConstructionSitePage.createConstructionSite")} dropdownItems={[]} getDropdownItemDisplayText={_temp3} getDropdownItemKey={_temp4} onCreateEntity={handleCreateConstructionSiteClicked} data-testid="CreateWorkTimeEntrySelectConstructionSitePage.CreateConstructionSiteButton" />];
    };
    $[5] = getRequiredCurrentUserPermissions;
    $[6] = handleCreateConstructionSiteClicked;
    $[7] = t;
    $[8] = workTimeEntryToCreate.userGroupId;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const renderRightContentElements = t4;
  let t5;
  if ($[10] !== t) {
    t5 = t("components.page.CreateWorkTimeEntrySelectConstructionSitePage.title");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== renderRightContentElements || $[13] !== t5) {
    t6 = <PageContentHeader title={t5} iconConfig={iconConfigurations.prefixed.page.CreateWorkTimeEntrySelectConstructionSitePage.titleIcon} renderRightContentElements={renderRightContentElements} />;
    $[12] = renderRightContentElements;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t) {
    t7 = t("components.page.CreateWorkTimeEntrySelectConstructionSitePage.searchInputPlaceholder");
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== handleSearchInputBlur || $[18] !== handleSearchInputFocus || $[19] !== searchText || $[20] !== t7) {
    t8 = <SearchInput placeholder={t7} value={searchText} className={styles.searchInput} onInput={setSearchText} onFocus={handleSearchInputFocus} onBlur={handleSearchInputBlur} data-testid="CreateWorkTimeEntrySelectConstructionSitePage.SearchInput" />;
    $[17] = handleSearchInputBlur;
    $[18] = handleSearchInputFocus;
    $[19] = searchText;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== constructionSites || $[23] !== searchText || $[24] !== showSearchContent) {
    t9 = showSearchContent ? <CreateWorkTimeEntrySelectConstructionSitePageSearchContent constructionSites={constructionSites} searchText={searchText} /> : <CreateWorkTimeEntrySelectConstructionSitePageDefaultContent constructionSites={constructionSites} />;
    $[22] = constructionSites;
    $[23] = searchText;
    $[24] = showSearchContent;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t6 || $[27] !== t8 || $[28] !== t9) {
    t10 = <PageContentContainer>{t6}{t8}{t9}</PageContentContainer>;
    $[26] = t6;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
});
function _temp(builder) {
  return builder.addHomePage().addCreateWorkTimeEntryPage().addCreateWorkTimeEntrySelectConstructionSitePage();
}
function _temp2(cs) {
  return !cs.archived;
}
function _temp3() {
  return "";
}
function _temp4() {
  return "";
}